import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle, Clock, Package, ShoppingCart, Cpu, Gauge,
  CheckCircle, XCircle, TrendingUp, ChevronRight, RefreshCw,
  AlertCircle, FileText, Anchor, Wrench
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, type, onClick }) {
  const iconClass = type === 'danger' ? 'danger' : type === 'warning' ? 'warning' : type === 'success' ? 'success' : type === 'info' ? 'info' : 'accent';
  return (
    <div className="stat-card clickable" onClick={onClick}>
      <div className={`stat-icon ${iconClass}`}><Icon /></div>
      <div className="stat-content">
        <div className={`stat-value ${type === 'danger' ? 'danger' : ''} ${type === 'warning' ? 'warning' : ''}`}>{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, vessel } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');

  const isShore = ['fleet_superintendent', 'technical_manager', 'procurement_officer', 'shore_admin'].includes(user?.role);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard');
      setData(res.data);
    } catch (e) {
      console.error('Dashboard load failed', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div><p>Loading dashboard...</p></div>;
  }

  if (!data) return null;

  // Shore / Fleet dashboard
  if (data.type === 'shore') {
    return (
      <div>
        <div className="flex-between mb-24">
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>Fleet Overview</h2>
            <p className="text-secondary text-sm">All vessels — {data.fleetStats.totalVessels} ships</p>
          </div>
          <button className="btn btn-outline" onClick={loadDashboard}><RefreshCw size={15} /> Refresh</button>
        </div>

        <div className="stat-cards">
          <StatCard icon={Anchor} label="Total Vessels" value={data.fleetStats.totalVessels} />
          <StatCard icon={AlertTriangle} label="Total Overdue Jobs" value={data.fleetStats.totalOverdue} type="danger" />
          <StatCard icon={XCircle} label="Open Defects" value={data.fleetStats.totalOpenDefects} type="warning" />
          <StatCard icon={Cpu} label="AI Alerts" value={data.fleetStats.totalAiAlerts} type="warning" />
        </div>

        <div className="fleet-grid">
          {data.vessels.map(v => (
            <div key={v.id} className="fleet-card clickable" onClick={() => {}}>
              <div className="fleet-card-header">
                <h4>{v.name}</h4>
                <p>IMO: {v.imo_number}</p>
              </div>
              <div className="fleet-card-body">
                <div className="fleet-stats">
                  <div className="fleet-stat">
                    <div className="val" style={{ color: v.overdue_jobs > 0 ? 'var(--danger)' : 'var(--success)' }}>{v.overdue_jobs}</div>
                    <div className="lbl">Overdue</div>
                  </div>
                  <div className="fleet-stat">
                    <div className="val" style={{ color: v.open_defects > 0 ? 'var(--warning)' : 'var(--success)' }}>{v.open_defects}</div>
                    <div className="lbl">Defects</div>
                  </div>
                  <div className="fleet-stat">
                    <div className="val" style={{ color: v.ai_alerts_pending > 0 ? '#7c3aed' : 'var(--success)' }}>{v.ai_alerts_pending}</div>
                    <div className="lbl">AI Alerts</div>
                  </div>
                  <div className="fleet-stat">
                    <div className="val">{v.completed_jobs_30d}</div>
                    <div className="lbl">Done (30d)</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {data.fleetAlerts.length > 0 && (
          <div className="card mt-16">
            <div className="card-header">
              <h3>AI Fleet Alerts</h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Vessel</th><th>WO Number</th><th>Job</th><th>Health Score</th><th>Confidence</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {data.fleetAlerts.map(a => (
                    <tr key={a.id}>
                      <td>{a.vessel_name}</td>
                      <td><span className="badge badge-ai">{a.wo_number}</span></td>
                      <td>{a.job_title}</td>
                      <td>
                        <span className={`badge ${a.ai_health_score < 25 ? 'badge-critical' : a.ai_health_score < 50 ? 'badge-watch' : 'badge-healthy'}`}>
                          {a.ai_health_score}
                        </span>
                      </td>
                      <td>{a.ai_confidence}%</td>
                      <td style={{ maxWidth: 300, fontSize: 12, color: 'var(--text-secondary)' }}>{a.ai_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vessel dashboard
  const { cards, jobsDueToday, upcomingJobs, aiAlerts, openDefects, lowStockItems, certificateAlerts } = data;

  return (
    <div>
      <div className="flex-between mb-24">
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>
            {vessel?.name || 'Vessel'} — Daily Briefing
          </h2>
          <p className="text-secondary text-sm">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            &nbsp;&nbsp;·&nbsp;&nbsp;
            <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }}></span>
              Online — Synced
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={loadDashboard}><RefreshCw size={15} /> Refresh</button>
          <button className="btn btn-accent" onClick={() => navigate('/work-orders')}><Wrench size={15} /> New Work Order</button>
        </div>
      </div>

      {/* Alert Banner */}
      {(cards.overdueCount > 0 || cards.aiAlerts > 0) && (
        <div className="alert alert-danger mb-16">
          <AlertCircle size={18} />
          <div>
            <strong>Attention Required:</strong> {cards.overdueCount} overdue job(s) and {cards.aiAlerts} AI alert(s) need your review.
            <button className="btn btn-danger btn-sm" style={{ marginLeft: 12 }} onClick={() => navigate('/work-orders')}>View Work Orders</button>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="stat-cards">
        <StatCard icon={AlertTriangle} label="Overdue Work Orders" value={cards.overdueCount} type="danger" sub={`${cards.overdueCount > 0 ? 'Needs immediate attention' : 'All on schedule'}`} onClick={() => navigate('/work-orders?status=Open&filter=overdue')} />
        <StatCard icon={Clock} label="Due This Week" value={cards.dueThisWeek} type={cards.dueThisWeek > 5 ? 'warning' : 'info'} onClick={() => navigate('/work-orders')} />
        <StatCard icon={XCircle} label="Open Defects" value={cards.openDefects} type={cards.openDefects > 0 ? 'warning' : 'success'} onClick={() => navigate('/inventory')} />
        <StatCard icon={Package} label="Low Stock Alerts" value={cards.lowStock} type={cards.lowStock > 0 ? 'warning' : 'success'} onClick={() => navigate('/inventory')} />
        <StatCard icon={ShoppingCart} label="Pending Requisitions" value={cards.pendingReqs} type="info" onClick={() => navigate('/purchasing')} />
        <StatCard icon={Cpu} label="AI Alerts" value={cards.aiAlerts} type={cards.aiAlerts > 0 ? 'warning' : 'success'} onClick={() => navigate('/work-orders?source=AI')} />
      </div>

      {/* Tabs */}
      <div className="tabs">
        <div className={`tab ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>
          <FileText size={15} />Jobs Due Today ({jobsDueToday.length})
        </div>
        <div className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`} onClick={() => setActiveTab('upcoming')}>
          <TrendingUp size={15} />Upcoming Jobs ({upcomingJobs.length})
        </div>
        <div className={`tab ${activeTab === 'ai' ? 'active' : ''}`} onClick={() => setActiveTab('ai')}>
          <Cpu size={15} />AI Alerts ({aiAlerts.length})
        </div>
        <div className={`tab ${activeTab === 'defects' ? 'active' : ''}`} onClick={() => setActiveTab('defects')}>
          <AlertTriangle size={15} />Defects ({openDefects.length})
        </div>
        <div className={`tab ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => setActiveTab('stock')}>
          <Package size={15} />Low Stock ({lowStockItems.length})
        </div>
      </div>

      {/* Jobs Due Today */}
      {activeTab === 'jobs' && (
        <div className="card">
          <div className="card-header">
            <h3>Jobs Due Today or Overdue</h3>
            <button className="btn btn-accent btn-sm" onClick={() => navigate('/work-orders')}>View All</button>
          </div>
          {jobsDueToday.length === 0 ? (
            <div className="empty-state">
              <CheckCircle />
              <h4>All caught up!</h4>
              <p>No jobs due today. Great work, Chief Engineer.</p>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>WO #</th><th>Job Title</th><th>Equipment</th><th>Dept</th><th>Due Date</th><th>Priority</th><th>Assigned</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {jobsDueToday.map(wo => (
                    <tr key={wo.id} className={`priority-${wo.priority.toLowerCase()}`} onClick={() => navigate(`/work-orders/${wo.id}`)} style={{ cursor: 'pointer' }}>
                      <td><span className="badge badge-ai">{wo.wo_number}</span></td>
                      <td><strong>{wo.job_title}</strong></td>
                      <td>{wo.component_name || '—'}</td>
                      <td>{wo.department}</td>
                      <td style={{ color: new Date(wo.due_date) < new Date() ? 'var(--danger)' : 'var(--text)' }}>
                        {wo.due_date}
                        {new Date(wo.due_date) < new Date() && <span style={{ fontSize: 10, color: 'var(--danger)' }}> OVERDUE</span>}
                      </td>
                      <td><span className={`badge badge-${wo.priority.toLowerCase()}`}>{wo.priority}</span></td>
                      <td>{wo.assigned_to}</td>
                      <td><span className={`badge badge-${wo.status.toLowerCase().replace(' ', '')}`}>{wo.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Upcoming */}
      {activeTab === 'upcoming' && (
        <div className="card">
          <div className="card-header"><h3>Upcoming Jobs (Next 7 Days)</h3></div>
          {upcomingJobs.length === 0 ? (
            <div className="empty-state"><Clock /><h4>No upcoming jobs</h4></div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>WO #</th><th>Job Title</th><th>Equipment</th><th>Due Date</th><th>Priority</th><th>Assigned</th></tr></thead>
                <tbody>
                  {upcomingJobs.map(wo => (
                    <tr key={wo.id} onClick={() => navigate(`/work-orders/${wo.id}`)} style={{ cursor: 'pointer' }}>
                      <td><span className="badge badge-open">{wo.wo_number}</span></td>
                      <td><strong>{wo.job_title}</strong></td>
                      <td>{wo.component_name || '—'}</td>
                      <td>{wo.due_date}</td>
                      <td><span className={`badge badge-${wo.priority.toLowerCase()}`}>{wo.priority}</span></td>
                      <td>{wo.assigned_to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* AI Alerts */}
      {activeTab === 'ai' && (
        <div className="card">
          <div className="card-header"><h3>AI-Predicted Jobs</h3><span className="badge badge-ai">AI Engine</span></div>
          {aiAlerts.length === 0 ? (
            <div className="empty-state"><Cpu /><h4>No AI alerts</h4><p>All equipment health scores are within acceptable range.</p></div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>WO #</th><th>Job</th><th>Health Score</th><th>Confidence</th><th>AI Reason</th><th>Due Date</th><th>Action</th></tr></thead>
                <tbody>
                  {aiAlerts.map(wo => (
                    <tr key={wo.id}>
                      <td><span className="badge badge-ai">{wo.wo_number}</span></td>
                      <td><strong>{wo.job_title}</strong></td>
                      <td>
                        <span className={`badge ${wo.ai_health_score < 25 ? 'badge-critical' : wo.ai_health_score < 50 ? 'badge-watch' : 'badge-healthy'}`}>
                          {wo.ai_health_score}
                        </span>
                      </td>
                      <td>{wo.ai_confidence}%</td>
                      <td style={{ maxWidth: 300, fontSize: 12, color: 'var(--text-secondary)' }}>{wo.ai_reason}</td>
                      <td>{wo.due_date}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-success btn-sm" onClick={() => navigate(`/work-orders/${wo.id}`)}>Accept</button>
                          <button className="btn btn-outline btn-sm">Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Defects */}
      {activeTab === 'defects' && (
        <div className="card">
          <div className="card-header"><h3>Open Defects</h3></div>
          {openDefects.length === 0 ? (
            <div className="empty-state"><CheckCircle /><h4>No open defects</h4></div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>Defect #</th><th>Description</th><th>Equipment</th><th>Severity</th><th>Raised Date</th></tr></thead>
                <tbody>
                  {openDefects.map(d => (
                    <tr key={d.id} className={`priority-${d.severity.toLowerCase()}`}>
                      <td>{d.defect_number}</td>
                      <td><strong>{d.description}</strong></td>
                      <td>{d.component_name || '—'}</td>
                      <td><span className={`badge badge-${d.severity.toLowerCase()}`}>{d.severity}</span></td>
                      <td>{d.raised_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Low Stock */}
      {activeTab === 'stock' && (
        <div className="card">
          <div className="card-header"><h3>Low Stock Items</h3></div>
          {lowStockItems.length === 0 ? (
            <div className="empty-state"><Package /><h4>All stock levels OK</h4></div>
          ) : (
            <div className="table-container">
              <table>
                <thead><tr><th>Part #</th><th>Part Name</th><th>Current</th><th>Min Stock</th><th>Unit</th><th>Status</th></tr></thead>
                <tbody>
                  {lowStockItems.map(s => (
                    <tr key={s.id}>
                      <td>{s.part_number}</td>
                      <td><strong>{s.part_name}</strong></td>
                      <td style={{ color: s.current_quantity <= 0 ? 'var(--danger)' : 'var(--warning)', fontWeight: 600 }}>{s.current_quantity}</td>
                      <td>{s.minimum_stock}</td>
                      <td>{s.unit_of_measure}</td>
                      <td><span className={`badge ${s.current_quantity <= 0 ? 'badge-critical' : 'badge-warning'}`}>{s.current_quantity <= 0 ? 'Zero' : 'Low'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Certificate Alerts */}
      {certificateAlerts && certificateAlerts.length > 0 && (
        <div className="card mt-16">
          <div className="card-header">
            <h3>Certificate Alerts</h3>
          </div>
          <div className="card-body">
            {certificateAlerts.map(cert => (
              <div key={cert.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <strong>{cert.certificate_name}</strong>
                  <span style={{ marginLeft: 8, color: 'var(--text-secondary)', fontSize: 12 }}>Expires: {cert.expiry_date}</span>
                </div>
                <span className={`badge badge-${cert.status.toLowerCase().replace(' ', '')}`}>{cert.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
